/**
 * When a message is due, and whether it is late.
 *
 * The API keeps a reply clock for most messages: an SLA ticket whose deadline
 * is 17:00 at the site on the Nth business day after arrival, counted with
 * Yale's holidays (enact-backend `domain/tickets.ts`). That clock is the
 * source whenever one is open.
 *
 * Some messages have none — rows from before their form had a clock, and
 * contact preferences, which the API deliberately never opens one for — yet
 * their type still promises a reply time ("Within 1 business day", from
 * `/meta`). For those the deadline is worked out here, from the day the
 * message arrived, and marked as an estimate. It counts business days the way
 * the API does — weekends and Yale's holidays skipped — from the copy of the
 * holiday list below. New ticket's "due …" preview uses the same count.
 */

const SITE_TZ = 'America/New_York';
/** The hour a business day's clock runs out, at the site. */
const CLOSE_HOUR = 17;

export type DueState = 'overdue' | 'today' | 'upcoming';

export interface Due {
  /** The site date it is due, YYYY-MM-DD. */
  date: string;
  state: DueState;
  /** Worked out here from the reply time, because no clock is open for it. */
  estimated: boolean;
}

const siteFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: SITE_TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  hourCycle: 'h23',
});

/** The site's calendar date and hour for an instant. */
function siteParts(at: Date): { date: string; hour: number } {
  const parts = siteFormat.formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return { date: `${get('year')}-${get('month')}-${get('day')}`, hour: Number(get('hour')) };
}

/** Calendar arithmetic on a YYYY-MM-DD date, at noon UTC so no zone can move it. */
function addDays(date: string, n: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The days Yale is closed — a copy of `HOLIDAYS_2026_2027` in enact-backend
 * `src/domain/tickets.ts`, which is the source. The API serves no route for
 * the list, so the two must be kept in step by hand: when the API's list is
 * extended (it runs out in December 2027) or the lab drops the recess days,
 * change this one in the same way, or a ticket's previewed due date and the
 * one the API stores will disagree around that holiday.
 */
export const YALE_HOLIDAYS: readonly string[] = [
  // 2026
  '2026-01-01', // New Year's Day
  '2026-01-19', // Martin Luther King Jr. Day
  '2026-04-03', // Good Friday
  '2026-05-25', // Memorial Day
  '2026-06-19', // Juneteenth
  '2026-07-03', // Independence Day (4 July is a Saturday)
  '2026-09-07', // Labor Day
  '2026-11-26', // Thanksgiving
  '2026-11-27', // Recess day
  '2026-12-24', // Recess day
  '2026-12-25', // Christmas Day
  '2026-12-28', // Recess day
  '2026-12-29', // Recess day
  '2026-12-30', // Recess day
  '2026-12-31', // Recess day
  // 2027
  '2027-01-01', // New Year's Day
  '2027-01-18', // Martin Luther King Jr. Day
  '2027-03-26', // Good Friday
  '2027-05-31', // Memorial Day
  '2027-06-18', // Juneteenth
  '2027-07-05', // Independence Day (4 July is a Sunday)
  '2027-09-06', // Labor Day
  '2027-11-25', // Thanksgiving
  '2027-11-26', // Recess day
  '2027-12-24', // Christmas Day (25 December is a Saturday)
  '2027-12-27', // Recess day
  '2027-12-28', // Recess day
  '2027-12-29', // Recess day
  '2027-12-30', // Recess day
  '2027-12-31', // Recess day
];

function isBusinessDay(date: string): boolean {
  const day = new Date(`${date}T12:00:00Z`).getUTCDay();
  return day !== 0 && day !== 6 && !YALE_HOLIDAYS.includes(date);
}

/**
 * `n` business days after `from`, counting from the next day — the API's rule
 * (`addBusinessDays` in `domain/tickets.ts`), so one business day on a Friday
 * is the Monday, and on the day before Thanksgiving is the Monday after it.
 */
export function addBusinessDays(from: string, n: number): string {
  let date = from;
  let left = n;
  while (left > 0) {
    date = addDays(date, 1);
    if (isBusinessDay(date)) left--;
  }
  return date;
}

/** Today's date at the site, YYYY-MM-DD — the day the API counts a due date from. */
export function siteToday(now: Date = new Date()): string {
  return siteParts(now).date;
}

/** The deadline of an open clock, as the API set it. */
export function dueFromClock(dueAt: string, now: Date = new Date()): Due {
  const at = new Date(dueAt);
  const date = siteParts(at).date;
  const state: DueState =
    now.getTime() > at.getTime() ? 'overdue' : siteParts(now).date === date ? 'today' : 'upcoming';
  return { date, state, estimated: false };
}

/** A deadline worked out from the day a message arrived and its reply time. */
export function dueFromArrival(createdAt: string, days: number, now: Date = new Date()): Due {
  const date = addBusinessDays(siteParts(new Date(createdAt)).date, days);
  const today = siteParts(now);
  const state: DueState =
    today.date > date || (today.date === date && today.hour >= CLOSE_HOUR)
      ? 'overdue'
      : today.date === date
        ? 'today'
        : 'upcoming';
  return { date, state, estimated: true };
}

/** "Within 3 business days" → 3; null for a reply time with no day count. */
export function replyDays(sla: string | undefined): number | null {
  const m = sla?.match(/(\d+)\s+business\s+days?/i);
  return m ? Number(m[1]) : null;
}

/** "Sep 23" for a site date. */
export function dueLabel(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * A plain due date — an action item's — judged on the site's calendar. Due
 * today counts as needing attention, as it does for a message.
 */
export function dueOn(date: string, now: Date = new Date()): Due {
  const today = siteParts(now).date;
  const state: DueState = today > date ? 'overdue' : today === date ? 'today' : 'upcoming';
  return { date, state, estimated: false };
}
